/* eslint-disable react-hooks/exhaustive-deps */
import { IExtLocation, IGResponse, ILocation, LeafletLayerName } from "@common/models";
import { IAttraction } from "@common/models/CMS/Attraction";
import { IAttractionCategory } from "@common/models/CMS/AttractionCategory";
import { IEvent, IHtmlBody, IMedia, IPage } from "@common/models/CMS";
import { IMapPolygen } from "@common/models/CMS/MapPolygon";
import { IMapWidget } from "@common/models/CMS/MapWidget";
import { IMarker } from "@common/models/CMS/Marker";
import { IMarkerGroup } from "@common/models/CMS/MarkerGroup";
import { showGoogleMarkerInfoWindow } from "@components/general/GoogleMap";
import Map from "@components/general/Map";
import { getFilePath, IViewport, MapType } from "@config/appConfig";
import { IAppState } from "@misc/State";
import Config from "@service/Config";
import { apolloQuery } from "@service/graphql";
import { mapAttraction } from "@service/graphql/query/Query.Map";
import { MARKER_CONTENT } from "@service/graphql/query/Query.MarkerContent";
import { FC, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import CitySection from "./CitySection";
import DataSourceTags from "./DataSourceTags";
import { MapWidgetAction, MapWidgetContext, MarkerGroups, reducer, SearchSection } from "./index";

interface IMapWidgetProps {
    dataSource: IMapWidget;
}

export const MapWidget: FC<IMapWidgetProps> = ({ dataSource: mapWidget }) => {
    const { pathname } = useLocation();
    const mapWidgetRef = useRef<HTMLDivElement>(null);
    const [markersWithContents, setMarkersWithContents] = useState<IMarker[]>([]);
    const [currentGoogleMapOptions, setCurrentGoogleMapOptions] = useState<google.maps.MapOptions>();
    const { width } = useSelector((state: IAppState) => ({ width: state.windowWidth }));
    const { medium } = Config.get<IViewport>('viewport');
    const initialGoogleMap: google.maps.MapOptions = width > medium ? { zoom: mapWidget?.zoom, center: { lat: mapWidget.center?.latitude || 0, lng: mapWidget.center?.longitude || 0 } }
        : { zoom: 5, center: { lat: 31.0817841, lng: 53.07171 } }
    const [state, dispatch] = useReducer(reducer, {
        dataSource: [],
        markerGroups: [],
        polys: mapWidget?.layers?.contentItems?.flatMap(layer => layer.overlies?.contentItems || {})
            .concat(mapWidget?.layers?.contentItems?.flatMap(layer => layer.overlies?.contentItems.filter(p => p.invert)
                .map(p => ({ ...p, invert: false, fillOpacity: 0, stroke: "#c9c9c9", strokeWidth: 3, strokeOpacity: 1 })) || {})) || [],
        selectedMarkerGroups: [],
        mapMarkers: [],
    });

    useEffect(() => {
        mapDataSourceTagsToMarkerGroups(mapWidget?.dataSource || [])
            .then((response) => {
                dispatch({ type: MapWidgetAction.MarkerGroups, payload: { markerGroups: response } });
            })

        return (() => {
            document.getElementById('header')?.style.removeProperty('background-color');
        })
    }, [])

    useEffect(() => {
        onChangeMapMarkerGroups(state.markerGroups);
    }, [state.markerGroups])

    useEffect(() => {
        if (state.currentMarker?.contentItemId) {
            setCurrentGoogleMapOptions({
                zoom: 9,
                center: { lat: state.currentMarker?.location?.latitude || 0, lng: state.currentMarker?.location?.longitude || 0 }
            });
            document.getElementById('header')?.style?.setProperty('background-color', "#ffffff");
        }
        else {
            setCurrentGoogleMapOptions(initialGoogleMap);
            document.getElementById('header')?.style.removeProperty('background-color');
        }
    }, [state.currentMarker])

    const onChangeMapMarkerGroups = (selectedMarkerGroups: IMarkerGroup[]) => {
        const mapMarkers: IExtLocation[] = [];
        state?.markerGroups?.filter(markerGroup => !selectedMarkerGroups.length || selectedMarkerGroups.some(x => x.contentItemId === markerGroup.contentItemId)).forEach(markerGroup => markerGroup.markers?.contentItems.forEach((marker: IMarker) =>
            mapMarkers.push({
                id: marker.contentItemId,
                icon: getFilePath(marker.icon?.paths[0] ?? markerGroup.defaultMarkerIcon?.paths[0]),
                lat: marker.location?.latitude as number,
                lng: marker.location?.longitude as number,
                tooltip: marker.tooltip,
                tooltipDisplayMode: marker.tooltipDisplayMode,
            })));
        dispatch({ type: MapWidgetAction.MapMakers, payload: { mapMarkers: mapMarkers } });
    }

    const mapDataSourceTagsToMarkerGroups = (dataSourceTags: string[]): Promise<IMarkerGroup[]> => {
        return new Promise((resolve) => {
            const markerGroups: IMarkerGroup[] = [];
            if (dataSourceTags.some(x => x === "Custom")) {
                mapWidget.markerGroups?.contentItems?.forEach(markerGroup => markerGroups.push({
                    ...markerGroup,
                    markers: { contentItems: markerGroup.markers?.contentItems?.map(marker => ({ ...marker, tag: "Custom" })) || [] }
                }));
                if (dataSourceTags.length === 1)
                    return resolve(markerGroups);
            }
            apolloQuery<IGResponse<IAttraction[] | IEvent[]>>(mapAttraction({
                attraction: dataSourceTags.some(x => x === "Attractions"),
                event: dataSourceTags.some(x => x === "Events")
            }))
                .then((response) => {
                    const attractions: IAttraction[] = response.data.attraction as IAttraction[];
                    dispatch({ type: MapWidgetAction.DataSource, payload: { dataSource: attractions } });
                    attractions.forEach((attraction) => {
                        const category: IAttractionCategory = attraction.category?.contentItems.length && attraction.category.contentItems[0].parent?.contentItems.length ?
                            attraction.category.contentItems[0].parent.contentItems[0] : attraction.category?.contentItems[0] || {};
                        if (category.contentItemId && !markerGroups.some(x => x.contentItemId === category.contentItemId)) {
                            markerGroups.push({
                                contentItemId: category.contentItemId,
                                defaultMarkerIcon: category?.mapMarkerIcon,
                                displayText: category.displayText,
                                markers: { contentItems: [] }
                            })
                        }
                        markerGroups.find(x => x.contentItemId === category.contentItemId)?.markers?.contentItems.push({
                            contentItemId: attraction.localization?.localizationSet,
                            tag: "Attractions",
                            icon: attraction.category?.contentItems.length ? attraction.category.contentItems[0].mapMarkerIcon : {} as IMedia,
                            location: attraction.location,
                            tooltipDisplayMode: "OnHover",
                            tooltip: { html: `<h3>${attraction.displayText}</h3>` },
                            content: { cover: attraction.cover as IMedia, introduction: attraction.introduction as IHtmlBody }
                        })
                    })
                    resolve(markerGroups);
                })
        })
    }

    const onChangeDataSourceTagsGroups = (dataSourceTags: string[]) => {
        mapDataSourceTagsToMarkerGroups(dataSourceTags.length ? dataSourceTags : mapWidget.dataSource || [])
            .then((response) => {
                dispatch({ type: MapWidgetAction.MarkerGroups, payload: { markerGroups: response } });
            })
    }

    const onMarkerClick = (marker: IExtLocation) => {
        showGoogleMarkerInfoWindow(marker?.id || "");
        dispatch({
            type: MapWidgetAction.CurrentMarker, payload: {
                currentMarker: {}
            }
        });
        const markerGroup: IMarkerGroup = state.markerGroups?.find(markerGroup => markerGroup?.markers?.contentItems?.some(x => x.contentItemId === marker.id)) || {};
        dispatch({
            type: MapWidgetAction.CurrentMarker, payload: {
                currentMarker: markerGroup?.markers?.contentItems.find(x => marker.id === x.contentItemId)
            }
        });
        if (mapWidget.markerGroups?.contentItems.find(markerGroup => markerGroup.markers?.contentItems.some(x => x.contentItemId === marker.id))) {
            apolloQuery<IGResponse<IPage[]>>(MARKER_CONTENT, { query: MARKER_CONTENT, variables: { path_in: pathname } })
                .then((response) => {
                    const markersWithContents: IMarker[] = [];
                    const mapWidgetSource = response.data.page[0].content?.contentItems.find(x => x.contentType === "MapWidget") as IMapWidget;
                    mapWidgetSource.markerGroups?.contentItems.forEach(markerGroup => markerGroup.markers?.contentItems.forEach(x => markersWithContents.push(x)));
                    setMarkersWithContents(markersWithContents);
                })
        }
    }

    return (
        <MapWidgetContext.Provider
            value={useMemo(() => { return { state, dispatch } }, [state, dispatch])}
        >
            <div ref={mapWidgetRef} className="map-widget" style={{ height: "100vh" }}>
                <Map
                    mode={mapWidget?.provider === MapType[MapType.Google] ? MapType.Google : MapType.Leaflet}
                    googleMapOption={{
                        initial: {
                            zoomControl: false,
                            scaleControl: false,
                            draggable: mapWidget.zoomEnable,
                            fullscreenControl: false,
                            mapTypeControl: false,
                            streetViewControl: false,
                            zoom: initialGoogleMap.zoom,
                            minZoom: initialGoogleMap.zoom,
                            mapTypeId: mapWidget.style?.toLowerCase(),
                            center: initialGoogleMap.center
                        },
                        current: {
                            zoom: currentGoogleMapOptions?.zoom || initialGoogleMap.zoom,
                            center: currentGoogleMapOptions?.center || initialGoogleMap.center
                        }
                    }}
                    googlePolygons={state.polys.map(p => {
                        return {
                            id: p.contentItemId,
                            invert: p.invert,
                            paths: getPolygonPath(p),
                            strokeColor: p.stroke,
                            strokeWeight: p.strokeWidth,
                            strokeOpacity: p.strokeOpacity,
                            fillColor: p.fill,
                            fillOpacity: p.fillOpacity
                        }
                    })}
                    leafletLayerName={LeafletLayerName.StandardLayer}
                    leafletMapOption={{
                        initial: {
                            dragging: false,
                            zoomControl: false,
                            scrollWheelZoom: false,
                            zoom: mapWidget?.zoom,
                            center: {
                                lat: mapWidget.center?.latitude || 0,
                                lng: mapWidget.center?.longitude || 0
                            }
                        }
                    }}
                    markers={state.mapMarkers}
                    onMarkerClick={onMarkerClick}
                />
                <SearchSection
                    markersWithContents={markersWithContents}
                />
                {
                    mapWidget.dataSource?.some(x => x === "Cities") &&
                    <CitySection />
                }
                <div className="content-section flex flex-start column-direction">
                    {
                        (mapWidget?.dataSource?.filter(x => x !== "Cities").length || 0) > 1 &&
                        <DataSourceTags
                            dataSource={mapWidget?.dataSource?.filter(x => x !== "Cities") || []}
                            onChangeDataSourceTagsGroups={onChangeDataSourceTagsGroups}
                        />
                    }
                    {
                        (state.markerGroups.length || 0) > 1 &&
                        <MarkerGroups
                            title={mapWidget.displayText}
                            dataSource={state.markerGroups}
                            onChangeMapMarkerGroups={onChangeMapMarkerGroups}
                        />
                    }
                </div>
            </div >
        </MapWidgetContext.Provider>
    )
}

export function getPolygonPath(polygon: IMapPolygen) {
    if (polygon.points) {
        const points = JSON.parse(polygon.points || "") as number[][];
        if (polygon.invert) {
            points.unshift([0, 90], [180, 90], [180, -90], [0, -90], [-180, -90], [-180, 0], [-180, 90], [0, 90]);
            points.push([0, 90]);
        }
        const path: ILocation[] = [];
        for (let i = 0; i < points.length; i++) {
            path.push({ lat: points[i][1], lng: points[i][0] });
        }
        return path.slice();
    }
}